/**
 * useAgentChat — minimal HTTP/SSE chat hook.
 *
 * Posts to /api/agent/:userId/chat and consumes the AI SDK
 * UI message stream (data: <json>\n\n) line-by-line.
 *
 * Tracks reasoning + tool-call activity per assistant message so the UI
 * can render "thinking…" hints and tool chips while streaming.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { authHeaders, clearPasscode } from "../lib/agent-api";

const API_BASE =
	(import.meta.env.VITE_AGENT_HTTP_URL as string | undefined) ??
	"https://boring-agent.codyboring.workers.dev";

const DRAFT_KEY = "boring-agent-draft";
const USER_KEY = "boring-agent-user";

export type ToolCallStatus = "running" | "done" | "error";

export interface ToolCall {
	id: string;
	name: string;
	status: ToolCallStatus;
	inputPreview?: string;
	output?: string;
	errorText?: string;
}

export interface ChatMessage {
	id: string;
	role: "user" | "assistant";
	content: string;
	parts?: Array<{ type: string; text?: string; image?: string }>;
	reasoning?: string;
	toolCalls?: ToolCall[];
	timestamp: number;
}

export interface Attachment {
	id: string;
	name: string;
	src: string;
}

function generateId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getOrCreateUserId(): string {
	if (typeof localStorage === "undefined") return "anon";
	let id = localStorage.getItem(USER_KEY);
	if (!id) {
		id = generateId();
		localStorage.setItem(USER_KEY, id);
	}
	return id;
}

interface UIPart {
	type: string;
	text?: string;
	image?: string;
}

interface UIMessage {
	id: string;
	role: "user" | "assistant";
	parts: UIPart[];
	reasoning?: string;
	toolCalls?: ToolCall[];
}

function uiToChat(m: UIMessage): ChatMessage {
	const text = m.parts.find((p) => p.type === "text")?.text ?? "";
	return {
		id: m.id,
		role: m.role,
		content: text,
		parts: m.parts,
		reasoning: m.reasoning,
		toolCalls: m.toolCalls,
		timestamp: Date.now(),
	};
}

export function useAgentChat() {
	const [userId] = useState(getOrCreateUserId);
	const [uiMessages, setUiMessages] = useState<UIMessage[]>([]);
	const [input, setInput] = useState(() => {
		if (typeof localStorage === "undefined") return "";
		return localStorage.getItem(DRAFT_KEY) || "";
	});
	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [isStreaming, setIsStreaming] = useState(false);
	const [isThinking, setIsThinking] = useState(false);
	const abortRef = useRef<AbortController | null>(null);
	const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (draftTimer.current) clearTimeout(draftTimer.current);
		draftTimer.current = setTimeout(() => {
			if (typeof localStorage !== "undefined") localStorage.setItem(DRAFT_KEY, input);
		}, 500);
		return () => {
			if (draftTimer.current) clearTimeout(draftTimer.current);
		};
	}, [input]);

	const updateAssistantMessage = useCallback(
		(messageId: string, mutate: (m: UIMessage) => UIMessage) => {
			setUiMessages((prev) => {
				const idx = prev.findIndex((m) => m.id === messageId);
				if (idx === -1) return prev;
				const next = [...prev];
				next[idx] = mutate(next[idx]);
				return next;
			});
		},
		[]
	);

	const appendDelta = useCallback(
		(messageId: string, delta: string) => {
			updateAssistantMessage(messageId, (m) => {
				const parts = [...m.parts];
				const lastTextIdx = parts.findLastIndex((p) => p.type === "text");
				if (lastTextIdx === -1) {
					parts.push({ type: "text", text: delta });
				} else {
					parts[lastTextIdx] = {
						...parts[lastTextIdx],
						text: (parts[lastTextIdx].text ?? "") + delta,
					};
				}
				return { ...m, parts };
			});
		},
		[updateAssistantMessage]
	);

	const appendReasoning = useCallback(
		(messageId: string, delta: string) => {
			updateAssistantMessage(messageId, (m) => ({
				...m,
				reasoning: (m.reasoning ?? "") + delta,
			}));
		},
		[updateAssistantMessage]
	);

	const upsertToolCall = useCallback(
		(messageId: string, call: Partial<ToolCall> & { id: string }) => {
			updateAssistantMessage(messageId, (m) => {
				const calls = [...(m.toolCalls ?? [])];
				const idx = calls.findIndex((c) => c.id === call.id);
				if (idx === -1) {
					calls.push({
						id: call.id,
						name: call.name ?? "tool",
						status: call.status ?? "running",
						inputPreview: call.inputPreview,
						output: call.output,
						errorText: call.errorText,
					});
				} else {
					calls[idx] = { ...calls[idx], ...call };
				}
				return { ...m, toolCalls: calls };
			});
		},
		[updateAssistantMessage]
	);

	const sendMessage = useCallback(
		async (text?: string): Promise<boolean> => {
			const messageText = (text ?? input).trim();
			if (!messageText && attachments.length === 0) return false;
			if (isStreaming || isThinking) return false;

			const userMsg: UIMessage = {
				id: generateId(),
				role: "user",
				parts: [{ type: "text", text: messageText }],
			};
			const assistantMsg: UIMessage = {
				id: generateId(),
				role: "assistant",
				parts: [],
				reasoning: undefined,
				toolCalls: [],
			};

			const next = [...uiMessages, userMsg];
			setUiMessages([...next, assistantMsg]);
			setInput("");
			setAttachments([]);
			if (typeof localStorage !== "undefined") localStorage.removeItem(DRAFT_KEY);

			setIsThinking(true);
			const controller = new AbortController();
			abortRef.current = controller;

			try {
				const res = await fetch(`${API_BASE}/api/agent/${userId}/chat`, {
					method: "POST",
					headers: { "content-type": "application/json", ...authHeaders() },
					body: JSON.stringify({ messages: next }),
					signal: controller.signal,
				});
				if (res.status === 401) {
					clearPasscode();
					window.dispatchEvent(new CustomEvent("agent-auth-required"));
					throw new Error("Unauthorized");
				}
				if (!res.ok || !res.body) {
					throw new Error(`Chat failed: ${res.status}`);
				}

				setIsThinking(false);
				setIsStreaming(true);

				const reader = res.body.getReader();
				const decoder = new TextDecoder();
				let buf = "";
				while (true) {
					const { done, value } = await reader.read();
					if (done) break;
					buf += decoder.decode(value, { stream: true });
					let nl: number;
					// biome-ignore lint/suspicious/noAssignInExpressions: SSE line loop
					while ((nl = buf.indexOf("\n")) !== -1) {
						const line = buf.slice(0, nl).trim();
						buf = buf.slice(nl + 1);
						if (!line) continue;
						const payload = line.startsWith("data:") ? line.slice(5).trim() : line;
						if (!payload || payload === "[DONE]") continue;
						try {
							const evt = JSON.parse(payload) as {
								type?: string;
								delta?: string;
								text?: string;
								toolCallId?: string;
								toolName?: string;
								input?: unknown;
								output?: unknown;
								errorText?: string;
								inputTextDelta?: string;
							};
							switch (evt.type) {
								case "text-delta":
									if (typeof evt.delta === "string") appendDelta(assistantMsg.id, evt.delta);
									break;
								case "reasoning-delta":
									if (typeof evt.delta === "string") appendReasoning(assistantMsg.id, evt.delta);
									break;
								case "tool-input-start":
									if (evt.toolCallId)
										upsertToolCall(assistantMsg.id, {
											id: evt.toolCallId,
											name: evt.toolName ?? "tool",
											status: "running",
										});
									break;
								case "tool-input-delta":
									if (evt.toolCallId && typeof evt.inputTextDelta === "string") {
										upsertToolCall(assistantMsg.id, {
											id: evt.toolCallId,
											inputPreview: evt.inputTextDelta.slice(0, 120),
										});
									}
									break;
								case "tool-input-available":
									if (evt.toolCallId)
										upsertToolCall(assistantMsg.id, {
											id: evt.toolCallId,
											name: evt.toolName ?? "tool",
											inputPreview:
												typeof evt.input === "string"
													? evt.input.slice(0, 120)
													: JSON.stringify(evt.input ?? {}).slice(0, 120),
											status: "running",
										});
									break;
								case "tool-output-available":
									if (evt.toolCallId)
										upsertToolCall(assistantMsg.id, {
											id: evt.toolCallId,
											status: "done",
											output:
												typeof evt.output === "string"
													? evt.output.slice(0, 200)
													: JSON.stringify(evt.output ?? {}).slice(0, 200),
										});
									break;
								case "tool-output-error":
									if (evt.toolCallId)
										upsertToolCall(assistantMsg.id, {
											id: evt.toolCallId,
											status: "error",
											errorText: evt.errorText,
										});
									break;
							}
						} catch {
							/* skip malformed line */
						}
					}
				}
			} catch (e) {
				if ((e as Error).name !== "AbortError") {
					appendDelta(assistantMsg.id, `\n\n_Error: ${(e as Error).message}_`);
				}
			} finally {
				setIsStreaming(false);
				setIsThinking(false);
				abortRef.current = null;
			}
			return true;
		},
		[
			input,
			attachments,
			isStreaming,
			isThinking,
			uiMessages,
			userId,
			appendDelta,
			appendReasoning,
			upsertToolCall,
		]
	);

	const stop = useCallback(() => {
		abortRef.current?.abort();
		abortRef.current = null;
		setIsStreaming(false);
		setIsThinking(false);
	}, []);

	const clearChat = useCallback(() => {
		stop();
		setUiMessages([]);
	}, [stop]);

	const setMessages = useCallback((next: ChatMessage[]) => {
		setUiMessages(
			next.map((m) => ({
				id: m.id,
				role: m.role,
				parts: m.parts ?? [{ type: "text", text: m.content }],
				reasoning: m.reasoning,
				toolCalls: m.toolCalls,
			}))
		);
	}, []);

	const addAttachment = useCallback((file: File, src: string) => {
		setAttachments((prev) => [...prev, { id: generateId(), name: file.name, src }]);
	}, []);
	const removeAttachment = useCallback((id: string) => {
		setAttachments((prev) => prev.filter((a) => a.id !== id));
	}, []);
	const clearAttachments = useCallback(() => setAttachments([]), []);

	const messages = uiMessages.map(uiToChat);

	return {
		messages,
		setMessages,
		input,
		setInput,
		isConnected: true,
		isStreaming,
		isThinking,
		attachments,
		addAttachment,
		removeAttachment,
		clearAttachments,
		sendMessage,
		stop,
		clearChat,
	};
}
